/**
 * `MissionTickerBridge` — always-mounted, renders nothing. Subscribes to the
 * runtime's `mission.progress` push and posts one infobar line per real
 * transition.
 *
 * A null-rendering COMPONENT rather than a bare hook because `App.tsx` mounts
 * its always-on bridges as JSX siblings; you cannot mount a hook beside
 * `<LastActiveContextSync/>`.
 *
 * Costs nothing while quiet: `useLexEventStream` rides a module-level event
 * bus fed by the gateway socket, so this adds ZERO new HTTP polling (I-1).
 *
 * ── Suppression (I-4, guarding the I-6 ruling amendment) ────────────────
 * Mission traffic must not render here while the Agentes Lexiun pane is on
 * screen — one surface at a time, never two. Two things make that correct:
 *
 *  1. It uses the SHARED `isRightSidebarOnScreen` predicate, because the
 *     pane branches on VIEWPORT first and then reads exactly one flag;
 *     `expanded || drawerOpen` silently over-suppresses on every phone.
 *  2. It reads that state at DISPATCH time via `getState()`, NOT from the
 *     component body. A value closed over at subscribe time would go stale
 *     the moment the sidebar opened, and the invariant would break silently
 *     — while still passing a test that only ever asserts absence.
 *
 * Suppression is at INGESTION, so opening the sidebar mid-message needs no
 * purge: lines already posted finish their TTL, new ones stop arriving.
 */
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLexEventStream } from "@/hooks/useEventStream";
import { isRightSidebarOnScreen } from "@/lib/right-sidebar-visibility";
import { createTickerLineSource } from "@/lib/mission-ticker-lines";
import { classifyViewport } from "@/hooks/useViewportSize";
import { useInfobarStore } from "@/stores/useInfobarStore";
import { useMissionsStore, type MissionProgressEvent } from "@/stores/useMissionsStore";
import { useUIStore } from "@/stores/useUIStore";

export function MissionTickerBridge() {
  const { t } = useTranslation();

  const source = useMemo(
    () =>
      createTickerLineSource({
        // Read the goal at LINE-BUILD time from the store, which the app-wide
        // pollers keep hydrated. Returning null (rather than the id) when the
        // mission is unknown is deliberate: an unnamed step is better than a
        // raw id in front of a lawyer.
        goalFor: (missionId) =>
          useMissionsStore.getState().missions.get(missionId)?.summary.goal ?? null,
        t: (key, vars) => String(t(key, vars as never)),
      }),
    [t],
  );

  const onProgress = useCallback(
    (payload: unknown) => {
      const ui = useUIStore.getState();
      const onScreen = isRightSidebarOnScreen(
        classifyViewport(typeof window === "undefined" ? 1400 : window.innerWidth),
        ui.rightSidebarExpanded,
        ui.rightDrawerOpen,
      );
      if (onScreen) return; // I-4 suppression, at ingestion

      const line = source.next(payload as MissionProgressEvent);
      if (!line) return;
      useInfobarStore.getState().post({
        key: `mission:${line.missionId}`,
        text: line.text,
        mode: line.mode,
        ttlMs: 6_000,
      });
    },
    [source],
  );

  useLexEventStream("mission.progress", onProgress);
  return null;
}
